(() => {
  'use strict';

  const KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'];
  const ATTR_KEY = 'umprotech-attribution-v2';
  const ATTR_TTL = 30 * 86400000;
  const DRAFT_TTL = 2 * 3600000;
  const VERSION = '2026.08-production-3';
  const DRAFT_ALLOWLIST = new Set([
    'contact[Request type]',
    'contact[Equipment type]',
    'contact[Purchase timing]',
    'contact[Preferred contact]',
    'contact[Production impact]',
    'contact[Time in business]',
    'contact[Financing purchase timing]',
    'contact[Ownership request]',
    'contact[Installation scope]',
  ]);

  const storage = (name) => {
    try { return window[name] || null; } catch { return null; }
  };
  const local = storage('localStorage');
  const session = storage('sessionStorage');
  const read = (store, key) => {
    if (!store) return null;
    try { return JSON.parse(store.getItem(key) || 'null'); } catch { return null; }
  };
  const write = (store, key, value) => {
    if (!store) return;
    try { store.setItem(key, JSON.stringify(value)); } catch { /* storage optional */ }
  };
  const remove = (store, key) => {
    if (!store) return;
    try { store.removeItem(key); } catch { /* storage optional */ }
  };
  const privacyAllowed = (kind) => {
    const privacy = window.Shopify?.customerPrivacy;
    const method = kind === 'marketing' ? 'marketingAllowed' : 'analyticsProcessingAllowed';
    try { return typeof privacy?.[method] === 'function' && privacy[method]() === true; } catch { return false; }
  };
  const track = (event, details = {}) => {
    if (!window.UmproThemeConfig?.analyticsEnabled || !privacyAllowed('analytics')) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...details });
    if (typeof window.umprotechTrack === 'function') window.umprotechTrack(event, details);
  };
  const randomHex = (length) => {
    const bytes = new Uint8Array(Math.ceil(length / 2));
    if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
    else bytes.forEach((_, index) => { bytes[index] = Math.floor(Math.random() * 256); });
    return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('').slice(0, length).toUpperCase();
  };
  const newLeadId = () => `UM-WEB-${new Date().toISOString().slice(0, 10).replaceAll('-', '')}-${randomHex(10)}`;
  const field = (form, name, value, key) => {
    let input = form.querySelector(`[data-prod-field="${CSS.escape(key)}"]`);
    if (!(input instanceof HTMLInputElement)) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.dataset.prodField = key;
      form.prepend(input);
    }
    input.value = value == null ? '' : String(value);
    return input;
  };
  const routeOf = (form, fallback = 'machine_quote') => form.querySelector('[data-route-field]')?.value || fallback;
  const campaignFrom = (params) => Object.fromEntries(KEYS.flatMap((key) => {
    const value = params.get(key);
    return value ? [[key, value.slice(0, 500)]] : [];
  }));
  const attribution = () => {
    const now = Date.now();
    const params = new URLSearchParams(location.search);
    const marketingAllowed = privacyAllowed('marketing');
    const campaign = marketingAllowed ? campaignFrom(params) : {};
    let referrerPath = '';
    if (marketingAllowed && document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        referrerPath = referrerUrl.origin === location.origin ? referrerUrl.pathname.slice(0, 500) : referrerUrl.origin.slice(0, 200);
      } catch { referrerPath = ''; }
    }
    const touch = {
      ...campaign,
      landing_page: location.pathname.slice(0, 500),
      referrer: referrerPath,
      captured_at: new Date().toISOString(),
    };
    if (!marketingAllowed) {
      remove(local, ATTR_KEY);
      return { first: touch, last: touch, expiresAt: now };
    }
    let value = read(local, ATTR_KEY);
    if (!value || Number(value.expiresAt) <= now) {
      value = { first: touch, last: touch, expiresAt: now + ATTR_TTL };
    } else if (Object.keys(campaign).length) {
      value.last = touch;
      value.expiresAt = now + ATTR_TTL;
    }
    write(local, ATTR_KEY, value);
    return value;
  };
  const honeypot = (form) => {
    const wrap = document.createElement('div');
    wrap.hidden = true;
    wrap.dataset.honeypotWrap = 'true';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.style.setProperty('display', 'none', 'important');
    const label = document.createElement('label');
    label.textContent = 'Website';
    const input = document.createElement('input');
    input.type = 'text';
    input.name = 'contact[Website]';
    input.hidden = true;
    input.autocomplete = 'off';
    input.tabIndex = -1;
    input.setAttribute('aria-hidden', 'true');
    input.dataset.honeypot = 'true';
    label.append(input);
    wrap.append(label);
    form.prepend(wrap);
    return input;
  };
  const eligible = (item) => {
    if (!(item instanceof HTMLInputElement || item instanceof HTMLSelectElement || item instanceof HTMLTextAreaElement)) return false;
    if (!item.name || item.disabled || item.type === 'hidden' || item.type === 'submit' || item.type === 'button') return false;
    return DRAFT_ALLOWLIST.has(item.name) && item.dataset.honeypot !== 'true';
  };
  const saveDraft = (form, key, leadId) => {
    const values = {};
    [...form.elements].forEach((item) => {
      if (!eligible(item)) return;
      values[item.name] = item instanceof HTMLInputElement && ['checkbox', 'radio'].includes(item.type) ? item.checked : item.value;
    });
    write(session, key, { expiresAt: Date.now() + DRAFT_TTL, route: routeOf(form), leadId, values });
  };
  const restoreDraft = (form, key) => {
    const draft = read(session, key);
    if (!draft || Number(draft.expiresAt) <= Date.now()) {
      remove(session, key);
      return null;
    }
    Object.entries(draft.values || {}).forEach(([name, value]) => {
      const item = form.elements.namedItem(name);
      if (!(item instanceof HTMLInputElement || item instanceof HTMLSelectElement || item instanceof HTMLTextAreaElement) || !DRAFT_ALLOWLIST.has(name)) return;
      if (item instanceof HTMLInputElement && ['checkbox', 'radio'].includes(item.type)) item.checked = Boolean(value);
      else item.value = String(value ?? '');
    });
    const selector = form.querySelector('[data-route-selector]');
    if (selector instanceof HTMLSelectElement && draft.route) {
      selector.value = draft.route;
      selector.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return draft;
  };

  document.addEventListener('DOMContentLoaded', () => {
    const attr = attribution();

    document.querySelectorAll('[data-smart-form-root]').forEach((root) => {
      const mode = root.dataset.formMode || 'smart';
      const draftKey = `umprotech-production-draft:${location.pathname}:${mode}`;
      const leadKey = `umprotech-production-lead:${location.pathname}:${mode}`;
      const success = root.querySelector('[data-form-success]');
      const form = root.querySelector('form.smart-form');

      if (!(form instanceof HTMLFormElement)) {
        if (success) {
          const submitted = read(session, leadKey) || {};
          track('lead_form_success_v2', {
            form_mode: mode,
            request_route: submitted.route || root.dataset.defaultRoute || 'machine_quote',
            page_path: location.pathname,
            lead_id: submitted.leadId || '',
          });
          remove(session, draftKey);
          remove(session, leadKey);
        }
        return;
      }

      const startedAt = Date.now();
      const restored = restoreDraft(form, draftKey);
      const storedLead = read(session, leadKey) || {};
      const leadId = restored?.leadId || storedLead.leadId || newLeadId();
      const trap = null;
      let started = false;
      let timer = 0;

      write(session, leadKey, { leadId, route: routeOf(form), expiresAt: Date.now() + DRAFT_TTL });
      field(form, 'contact[Lead ID]', leadId, 'lead-id');
      field(form, 'contact[Form version]', VERSION, 'form-version');
      field(form, 'contact[Client timezone]', Intl.DateTimeFormat().resolvedOptions().timeZone || '', 'timezone');
      field(form, 'contact[Client language]', navigator.language || '', 'language');
      field(form, 'contact[Viewport]', `${innerWidth}x${innerHeight}`, 'viewport');
      field(form, 'contact[First landing page]', attr.first?.landing_page || '', 'first-landing');
      field(form, 'contact[First referrer]', attr.first?.referrer || '', 'first-referrer');
      field(form, 'contact[First UTM source]', attr.first?.utm_source || '', 'first-source');
      field(form, 'contact[First UTM medium]', attr.first?.utm_medium || '', 'first-medium');
      field(form, 'contact[First UTM campaign]', attr.first?.utm_campaign || '', 'first-campaign');
      form.querySelectorAll('[data-tracking-key]').forEach((input) => {
        const key = input.dataset.trackingKey;
        if (key) input.value = attr.last?.[key] || attr.first?.[key] || '';
      });

      const saveSoon = () => {
        clearTimeout(timer);
        timer = setTimeout(() => saveDraft(form, draftKey, leadId), 250);
      };
      const start = () => {
        if (started) return;
        started = true;
        track('lead_form_start_v2', { form_mode: mode, request_route: routeOf(form), page_path: location.pathname });
      };
      form.addEventListener('input', (event) => {
        if (false) return;
        start();
        saveSoon();
      });
      form.addEventListener('change', (event) => {
        if (event.target === trap) return;
        start();
        saveSoon();
      });

      form.addEventListener('submit', (event) => {
        if (event.defaultPrevented) return;
        const route = routeOf(form);
        const elapsed = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
        field(form, 'contact[Submitted at]', new Date().toISOString(), 'submitted-at');
        field(form, 'contact[Form elapsed seconds]', elapsed, 'elapsed');
        const summary = form.querySelector('[data-summary-field]');
        if (summary instanceof HTMLTextAreaElement) {
          const extra = [
            `Lead ID: ${leadId}`,
            `First landing page: ${attr.first?.landing_page || ''}`,
            `First referrer: ${attr.first?.referrer || ''}`,
            `First UTM: ${[attr.first?.utm_source, attr.first?.utm_medium, attr.first?.utm_campaign].filter(Boolean).join(' / ')}`,
            `Elapsed seconds: ${elapsed}`,
          ];
          summary.value = [summary.value, ...extra].filter(Boolean).join('\n');
        }
        const subject = form.querySelector('[data-subject-field]');
        if (subject instanceof HTMLInputElement && !subject.value.includes(leadId)) subject.value = `${subject.value} | ${leadId}`;
        saveDraft(form, draftKey, leadId);
        write(session, leadKey, { leadId, route, expiresAt: Date.now() + DRAFT_TTL });
        track('lead_form_submit_v2', { form_mode: mode, request_route: route, page_path: location.pathname, lead_id: leadId });

        const button = form.querySelector('[data-submit-button]');
        const text = button?.querySelector('span');
        const previous = button?.dataset.idleLabel || text?.textContent || 'Send request';
        if (button instanceof HTMLButtonElement) {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        }
        if (text) text.textContent = root.dataset.submittingLabel || 'Sending request…';
        setTimeout(() => {
          if (!form.isConnected) return;
          if (button instanceof HTMLButtonElement) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          }
          if (text) text.textContent = previous;
        }, 20000);
      });

      track('lead_form_view_v2', { form_mode: mode, request_route: routeOf(form), page_path: location.pathname });
    });
  }, { once: true });

  document.addEventListener('visitorConsentCollected', () => {
    if (!privacyAllowed('marketing')) remove(local, ATTR_KEY);
  });
})();
